import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { ICorporatePart } from 'corporate/types';
import { useUpsertUomConversion } from 'parts/hooks';
import { uomService } from 'parts/services';
import { IUomConversion, UpsertUomConversionForm } from 'parts/types';

import { uomConversionSchema } from './schema';

interface IProps {
  corporatePart: Pick<ICorporatePart, 'part' | 'manufacturerId'>;
  uomConversion?: Partial<IUomConversion>;
  isOpen: boolean;
  onClose: () => void;
}

export const UomConversionForm = ({ corporatePart, uomConversion, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { upsertUomConversion } = useUpsertUomConversion();

  const formDefaultValues = useMemo(
    () =>
      uomConversion
        ? {
            ...uomConversion,
            fromUom: { id: uomConversion.fromUomId, name: uomConversion.fromUom },
          }
        : {},
    [uomConversion]
  );

  const getUomBaseOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
    uomService.getManyUoms({ ...requestPayload, filters: { isBase: 1 } }, signal);

  const onSubmit =
    (reset: UseFormReset<UpsertUomConversionForm>) =>
    async ({ fromUom, ...rest }: UpsertUomConversionForm) => {
      const { success } = await upsertUomConversion({
        partUomConversion: {
          ...rest,
          fromUom: fromUom.name,
          part: corporatePart.part,
          manufacturerId: corporatePart.manufacturerId,
        },
      });

      if (success) {
        handleOnClose(reset);
      }
    };

  const handleOnClose = (reset: UseFormReset<UpsertUomConversionForm>) => {
    clearNotification();
    onClose();
    reset();
  };

  return (
    <FormProvider
      schema={uomConversionSchema()}
      defaultValues={formDefaultValues}
      resetDefaultValues
    >
      <Form
        title={uomConversion?.id ? t('titleUpdateUomConversion') : t('titleAddUomConversion')}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      >
        <FormField
          name="fromUom"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelBaseUom'),
            required: true,
            disableClearable: true,
            getOptions: getUomBaseOptions,
            optionKey: 'name',
            enabledOnFocus: false,
          }}
        />
        <FormField
          name="toUom"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelAlternateUom'), required: true }}
        />
        <FormField
          name="multiplier"
          type={InputType.NUMBER_INPUT}
          inputProps={{ label: t('labelMultiplier'), required: true }}
        />
      </Form>
    </FormProvider>
  );
};
